import { Button, Empty, Spin, Tag, message as antdMessage } from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
  RobotOutlined,
} from "@ant-design/icons";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ContactAvatar } from "../components/ContactAvatar";
import { PageHeader } from "../components/PageHeader";
import { approveDraft, discardDraft, listDrafts } from "../api/ai";
import type { AiDraft } from "../api/ai";
import { relativeTime } from "../utils/time";
import { useWs } from "../hooks/useWs";
import { useIsMobile } from "../hooks/useBreakpoint";

export function AiDraftsPage() {
  const [drafts, setDrafts] = useState<AiDraft[]>([]);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState<string | null>(null);
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const refresh = () => {
    setLoading(true);
    listDrafts()
      .then((d) => setDrafts(d.drafts))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refresh();
  }, []);

  useWs((ev) => {
    if (ev.type === "ai:draft:done") refresh();
  });

  const onApprove = async (id: string) => {
    setPending(id);
    try {
      await approveDraft(id);
      antdMessage.success("已发送");
      refresh();
    } catch {
      antdMessage.error("发送失败");
    } finally {
      setPending(null);
    }
  };

  const onDiscard = async (id: string) => {
    setPending(id);
    try {
      await discardDraft(id);
      refresh();
    } finally {
      setPending(null);
    }
  };

  return (
    <>
      <PageHeader
        title="AI 草稿"
        subtitle={
          drafts.length > 0
            ? `${drafts.length} 条待审核 — 批准后通过 Gmail 发出。`
            : "AI 自动为「不重要联系人」起草的回复会出现在这里。"
        }
      />
      <div style={{ padding: 16, maxWidth: 880, margin: "0 auto" }}>
        {loading && drafts.length === 0 ? (
          <div style={{ padding: 32, display: "grid", placeItems: "center" }}>
            <Spin />
          </div>
        ) : drafts.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span style={{ color: "var(--ant-color-text-secondary)" }}>
                <RobotOutlined /> 暂无待审核草稿
              </span>
            }
          />
        ) : (
          <ul
            style={{
              listStyle: "none",
              margin: 0,
              padding: 0,
              display: "grid",
              gap: 12,
              gridTemplateColumns: "minmax(0, 1fr)",
            }}
          >
            {drafts.map((d) => (
              <li
                key={d.id}
                style={{
                  background: "var(--ant-color-bg-container)",
                  border: "1px solid var(--ant-color-border)",
                  borderRadius: 12,
                  padding: 16,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    gap: 12,
                    alignItems: "flex-start",
                    marginBottom: 12,
                  }}
                >
                  {d.message ? (
                    <ContactAvatar
                      email={d.message.fromEmail}
                      name={null}
                      tier="unimportant"
                      size={36}
                    />
                  ) : null}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <span
                        style={{
                          fontWeight: 600,
                          fontSize: 16,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          minWidth: 0,
                          flex: 1,
                        }}
                      >
                        {d.message?.fromEmail ?? "—"}
                      </span>
                      <span
                        style={{
                          fontSize: 14,
                          color: "var(--ant-color-text-secondary)",
                        }}
                      >
                        {relativeTime(d.createdAt)}
                      </span>
                    </div>
                    <div
                      style={{
                        fontSize: 15,
                        marginTop: 2,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        color: "var(--ant-color-text-secondary)",
                      }}
                    >
                      {d.thread?.subject ?? "(无主题)"} ·{" "}
                      {d.message?.snippet ?? ""}
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    background:
                      "color-mix(in srgb, var(--ant-color-primary) 6%, transparent)",
                    border:
                      "1px solid color-mix(in srgb, var(--ant-color-primary) 30%, transparent)",
                    borderRadius: 10,
                    padding: 12,
                    fontSize: 16,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                    lineHeight: 1.6,
                    minWidth: 0,
                  }}
                >
                  <Tag color="purple" style={{ marginBottom: 8 }}>
                    AI 草稿
                  </Tag>
                  <br />
                  {d.draftBody || (
                    <span style={{ color: "var(--ant-color-text-secondary)" }}>
                      (空草稿)
                    </span>
                  )}
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: isMobile ? "stretch" : "flex-end",
                    flexDirection: isMobile ? "column" : "row",
                    flexWrap: "wrap",
                    gap: 8,
                    marginTop: 12,
                  }}
                >
                  <Button
                    icon={<EditOutlined />}
                    onClick={() => navigate(`/?thread=${d.threadId}`)}
                    block={isMobile}
                  >
                    {isMobile ? "查看 / 编辑" : "去 Inbox 查看 / 编辑"}
                  </Button>
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      justifyContent: isMobile ? "stretch" : "flex-end",
                    }}
                  >
                    <Button
                      icon={<CloseOutlined />}
                      onClick={() => onDiscard(d.id)}
                      loading={pending === d.id}
                      block={isMobile}
                      style={isMobile ? { flex: 1 } : undefined}
                    >
                      丢弃
                    </Button>
                    <Button
                      type="primary"
                      icon={<CheckOutlined />}
                      onClick={() => onApprove(d.id)}
                      loading={pending === d.id}
                      disabled={!d.draftBody}
                      block={isMobile}
                      style={isMobile ? { flex: 2 } : undefined}
                    >
                      批准发送
                    </Button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}
